import {
  Box,
  useTheme,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from "@mui/material";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid2";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import dayjs from "dayjs";
import api from "services/api";

const LinguistRateUpdate = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, triggerPageRefresh } = useStateContext();

  const [effectiveDate, setEffectiveDate] = useState(dayjs());
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [futureServices, setFutureServices] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const linguistId = overlayData.LinguistID;
  const linguistName = overlayData.LinguistName;
  const newRate = overlayData.NewRate;

  const fetchFutureServices = useCallback(async () => {
    if (!linguistId || !effectiveDate) return;

    setIsFetching(true);
    setError(null);

    try {
      const response = await api.get(
        `/linguists/${linguistId}/futureServices`,
        {
          params: {
            fromDate: effectiveDate.format("YYYY-MM-DD"),
          },
        },
      );

      setFutureServices(response.data || []);
    } catch (err) {
      console.error("Error fetching future services:", err);
      setError("Failed to fetch future services. Please try again.");
    } finally {
      setIsFetching(false);
    }
  }, [effectiveDate, linguistId]);

  // Fetch future services when date changes
  useEffect(() => {
    fetchFutureServices();
  }, [fetchFutureServices]);

  const handleUpdateRates = async () => {
    if (!linguistId || !newRate || !effectiveDate) {
      setError("Missing required information");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post(
        `/linguists/${linguistId}/updateFutureRates`,
        {
          newRate: parseFloat(newRate),
          fromDate: effectiveDate.format("YYYY-MM-DD"),
        },
      );

      setSuccess(true);
      triggerPageRefresh(
        `Successfully updated ${response.data.updatedCount} service(s) with the new rate`,
        theme,
      );

      setTimeout(() => {
        setOverlayName("");
      }, 1500);
    } catch (err) {
      console.error("Error updating rates:", err);
      setError(
        err.response?.data?.message ||
          "Failed to update rates. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const groupedServices = futureServices.reduce((acc, service) => {
    const type = service.ServiceCategory || "Unknown";
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(service);
    return acc;
  }, {});

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onClick={() => setOverlayName("")}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.alt,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "900px",
          maxHeight: "85vh",
          display: "flex",
          borderRadius: "1.5rem",
          flexDirection: "column",
          color: theme.palette.text.primary,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: ".75rem 2rem",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <IconButton
              color="primary"
              size="large"
              onClick={() => setOverlayName("")}
              disabled={isLoading}
            >
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>
            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              Update Linguist Rate
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: "2rem",
            gap: 2,
            overflowY: "auto",
            height: "100%",
          }}
        >
          {/* Info Alert */}
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: theme.palette.info.main + "20",
              border: `1px solid ${theme.palette.info.main}`,
            }}
          >
            <Typography variant="body2" color="info.main">
              Linguist saved successfully! You can now update the rate on all
              future assignments.
            </Typography>
          </Box>

          {/* Linguist Info */}
          <Box>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Linguist
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {linguistName}
            </Typography>
          </Box>

          {/* New Rate Info */}
          <Box>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              New Rate
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="primary">
              ${parseFloat(newRate || 0).toFixed(2)} / hour
            </Typography>
          </Box>

          <Divider />

          {/* Date Picker */}
          <Box>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              Apply From Date
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select the date from which the new rate should be applied to all
              future assignments
            </Typography>
            <MobileDatePicker
              label="Effective Date"
              value={effectiveDate}
              onChange={(newValue) => setEffectiveDate(newValue)}
              disabled={isLoading}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: "outlined",
                },
              }}
            />
          </Box>

          {/* Error Message */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Success Message */}
          {success && (
            <Alert severity="success">
              Rates updated successfully! Closing...
            </Alert>
          )}

          {/* Future Services Summary */}
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Services to be Updated
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isFetching
                ? "Loading services..."
                : futureServices.length === 0
                  ? "No future services found for this linguist from the selected date."
                  : `${futureServices.length} service(s) will be updated with the new rate.`}
            </Typography>

            {/* Loading indicator */}
            {isFetching && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Services List */}
            {!isFetching && futureServices.length > 0 && (
              <Box sx={{ maxHeight: "300px", overflowY: "auto" }}>
                {Object.entries(groupedServices).map(([category, services]) => (
                  <Box key={category} sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Chip
                        label={category}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Typography variant="body2" color="text.secondary">
                        ({services.length})
                      </Typography>
                    </Box>
                    <Grid container spacing={1}>
                      {services.map((service) => (
                        <Grid size={12} key={service.id}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: theme.palette.background.default,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="semibold"
                              gutterBottom
                            >
                              {service.ProjectNumber} - {service.Name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Date: {dayjs(service.Date).format("MMM DD, YYYY")}
                              {" | "}
                              Current Rate: $
                              {parseFloat(service.Rate || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            p: "1.5rem 2rem",
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setOverlayName("")}
            disabled={isLoading}
          >
            Skip / Close
          </Button>
          <Button
            variant="contained"
            startIcon={
              isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveRoundedIcon />
              )
            }
            onClick={handleUpdateRates}
            disabled={
              isLoading || futureServices.length === 0 || !effectiveDate
            }
          >
            {isLoading
              ? "Updating..."
              : `Update ${futureServices.length} Service(s)`}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LinguistRateUpdate;
